import React from "react";
import logoDark from '../logo-transparent-dark.png';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faPhone, faEnvelope, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { useLocation, Link } from "react-router";
import { translations } from "../translations/footer";

const Footer = () => {
    const location = useLocation();

    const getLocalePrefix = () => {
        if (location.pathname === "/" || location.pathname.startsWith("/GE")) return "/GE";
        if (location.pathname.startsWith("/EN")) return "/EN";
        if (location.pathname.startsWith("/RU")) return "/RU";
        return "/GE";
    };

    const prefix = getLocalePrefix();
    const lang = location.pathname.split("/")[1] || "GE";
    const t = translations[lang] || translations["GE"];

    const handleAboutClick = (e) => {
        const isHomePage = location.pathname === prefix || location.pathname === '/' || location.pathname === `${prefix}/`;
        if (isHomePage) {
            e.preventDefault();
            const aboutEl = document.getElementById('about');
            if (aboutEl) {
                aboutEl.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <footer id='footer' className="relative w-full bg-stone-50/90 border-t border-stone-200/80 pt-16 pb-8 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                {/* 4-Column Modern Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 items-start">
                    
                    {/* Column 1: Brand & Slogan */}
                    <div className="flex flex-col items-start space-y-4">
                        <Link to={prefix} className="inline-block group" aria-label="Simi Winery Home">
                            <img 
                                src={logoDark} 
                                alt="Simi Wine Logo" 
                                className="h-16 sm:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105 filter drop-shadow-sm" 
                            />
                        </Link>
                        <p className="font-serif-georgian text-stone-600 text-sm leading-relaxed max-w-xs">
                            {t.slogan}
                        </p>
                    </div>

                    {/* Column 2: Navigation Links */}
                    <div className="flex flex-col items-start">
                        <h4 className="font-serif-georgian font-bold text-base tracking-wider text-stone-900 mb-4 pb-1.5 border-b-2 border-[#800020]/30">
                            {t.navTitle}
                        </h4>
                        <ul className="flex flex-col space-y-2.5 p-0 m-0 list-none">
                            <li>
                                <Link 
                                    to={prefix} 
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-1.5 group"
                                >
                                    <span className="text-[#800020]/40 group-hover:text-[#800020] transition-colors">›</span>
                                    <span>{t.home}</span>
                                </Link>
                            </li>
                            <li>
                                <a 
                                    href={`${prefix}#about`} 
                                    onClick={handleAboutClick}
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-1.5 group"
                                >
                                    <span className="text-[#800020]/40 group-hover:text-[#800020] transition-colors">›</span>
                                    <span>{t.about}</span>
                                </a>
                            </li>
                            <li>
                                <Link 
                                    to={`${prefix}/products`} 
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-1.5 group"
                                >
                                    <span className="text-[#800020]/40 group-hover:text-[#800020] transition-colors">›</span>
                                    <span>{t.products}</span>
                                </Link>
                            </li>
                            <li>
                                <a 
                                    href="#footer" 
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-1.5 group"
                                >
                                    <span className="text-[#800020]/40 group-hover:text-[#800020] transition-colors">›</span>
                                    <span>{t.contact}</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3: Contact Information (Vertical) */}
                    <div className="flex flex-col items-start">
                        <h4 className="font-serif-georgian font-bold text-base tracking-wider text-stone-900 mb-4 pb-1.5 border-b-2 border-[#800020]/30">
                            {t.contactTitle}
                        </h4>
                        <ul className="flex flex-col space-y-3.5 p-0 m-0 list-none">
                            <li>
                                <a 
                                    href="tel:+995592292828" 
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-3 group"
                                >
                                    <span className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center text-[#800020] group-hover:border-[#800020] group-hover:bg-[#800020] group-hover:text-white transition-all duration-300 shadow-sm">
                                        <FontAwesomeIcon icon={faPhone} className="text-xs" />
                                    </span>
                                    <span>{t.phone}</span>
                                </a>
                            </li>
                            <li>
                                <a 
                                    href="mailto:info@simiwine.com" 
                                    className="font-sans-georgian text-sm text-stone-600 hover:text-[#800020] transition-colors duration-200 flex items-center gap-3 group"
                                >
                                    <span className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center text-[#800020] group-hover:border-[#800020] group-hover:bg-[#800020] group-hover:text-white transition-all duration-300 shadow-sm">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                                    </span>
                                    <span>{t.email}</span>
                                </a>
                            </li>
                            <li>
                                <div className="font-sans-georgian text-sm text-stone-600 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center text-[#800020] shadow-sm">
                                        <FontAwesomeIcon icon={faLocationDot} className="text-xs" />
                                    </span>
                                    <span>{t.address}</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Column 4: Social Media */}
                    <div className="flex flex-col items-start">
                        <h4 className="font-serif-georgian font-bold text-base tracking-wider text-stone-900 mb-4 pb-1.5 border-b-2 border-[#800020]/30">
                            {t.socialTitle}
                        </h4>
                        <div className="flex flex-col space-y-2.5 w-full max-w-[200px]">
                            <a 
                                href="https://www.facebook.com/profile.php?id=61575706387899" 
                                target="_blank" 
                                rel="noreferrer"
                                className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white border border-stone-200 hover:border-[#800020] hover:bg-[#800020] text-stone-700 hover:text-white transition-all duration-300 shadow-sm group"
                            >
                                <FontAwesomeIcon icon={faFacebook} className="text-base text-[#800020] group-hover:text-white transition-colors" />
                                <span className="font-sans-georgian text-sm font-medium">Facebook</span>
                            </a>
                            <a 
                                href="https://www.instagram.com/simiwine/" 
                                target="_blank" 
                                rel="noreferrer"
                                className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white border border-stone-200 hover:border-[#800020] hover:bg-[#800020] text-stone-700 hover:text-white transition-all duration-300 shadow-sm group"
                            >
                                <FontAwesomeIcon icon={faInstagram} className="text-base text-[#800020] group-hover:text-white transition-colors" />
                                <span className="font-sans-georgian text-sm font-medium">Instagram</span>
                            </a>
                        </div>
                    </div>

                </div>

                {/* Elegant Minimalist Copyright Bar */}
                <div className="mt-14 pt-6 border-t border-black/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left text-xs text-stone-500 font-sans-georgian">
                    <p className="m-0">
                        &copy; {new Date().getFullYear()} Simi Wine. {t.rights}
                    </p>
                    <p className="m-0 font-medium text-stone-600">
                        {t.createdBy}{' '}
                        <a 
                            href="https://www.linkedin.com/in/bekadugladze/" 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-[#800020] hover:text-[#5a0016] font-semibold transition-colors duration-200 underline decoration-[#800020]/30 hover:decoration-[#800020]"
                        >
                            Beka Dugladze
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
